<script setup>
import { ref, onMounted, computed } from "vue";
import api from "@/services/apiService";
import { useRouter } from "vue-router";
import BackendPagination from "@/components/BackendPagination.vue";
import noDataImg from "@/assets/img/illustrations/noData.png";

const imgUrl = process.env.VUE_APP_API;

const houses = ref([]);
const loading = ref(true);
const errorMessage = ref("");

const router = useRouter();
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage)
);

const fetchHousesWithContacts = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/houses?page=${currentPage.value}`);
    const houseData = response.data.member ||[];
    totalItems.value = response.data.totalItems || 0;

    houseData.forEach(house => {
      house.contacts = (house.contacts || []).map(contact => ({
        ...contact,
        showDetails: false
      }));
    });

    houses.value = houseData;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Error al obtener las casas.";
  } finally {
    loading.value = false;
  }
};

const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    await fetchHousesWithContacts();
  }
};

onMounted(() => {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    router.push("/signin");
  } else {
    fetchHousesWithContacts();
  }
});
</script>

<template>
  <div class="container-fluid  card py-4">
    <div v-if="loading" class="text-center text-secondary">Cargando casas y contactos...</div>
    <div v-if="errorMessage" class="alert alert-danger text-dark">{{ errorMessage }}</div>
    <!-- Empty State -->
    <div v-if="houses.length === 0 && !loading && !errorMessage" class="text-center py-4">
      <img :src="noDataImg" alt="No hay datos disponibles" width="300" />
      <p class="text-secondary mt-3">No se encontraron casas con contactos.</p>
    </div>


    <div class="row  pt-3">
      <div class="col-12 mb-4" v-for="house in houses" :key="house.id">
        <div class="card shadow-sm p-3">
          <div class="row">
            <!-- Left: House Info -->
            <div class="col-md-6 border-end  d-flex  align-items-center">
              <div style="width: 200px; height: 120px;">
                <img v-if="!house.imageError" :src="`${imgUrl}/uploads/thumbnails/${house.thumbnail}`"
                  alt="Thumbnail" style="width: 200px; height: 120px; object-fit: cover; border-radius: 8px;"
                  @error="house.imageError = true" />
                <div v-else
                  style="width: 200px; height: 120px; background-color: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #999;">
                  📷 No picture
                </div>
              </div>

              <div class="ms-3">
                <h6 class="text-primary mb-2">{{ house.title }}</h6>
                <p class="mb-1"><strong>Dirección:</strong> {{ house.address }}</p>
                <p class="mb-1"><strong>Ciudad:</strong> {{ house.city.name }}, {{ house.city.country }}</p>
                <p class="mb-1"><strong>Precio:</strong> {{ house.price.toLocaleString() }} €</p>
              </div>
            </div>

            <!-- Right: Contacts -->
            <div class="col-md-6 ps-4 contacts-scroll-container">
              <h6 class="text-secondary mb-3">Contactos</h6>
              <div v-if="house.contacts.length === 0" class="text-muted">No hay contactos asociados.</div>

              <div v-else class="">
                <div v-for="contact in house.contacts" :key="contact['@id']" class="mb-3 p-2 border rounded bg-light">
                  <div class="d-flex justify-content-between align-items-center contact">
                    <div>
                      <strong>{{ contact.name }}</strong><br />
                      <small>{{ contact.email }}</small>
                    </div>
                    <button class="btn btn-sm btn-outline-secondary"
                      @click="contact.showDetails = !contact.showDetails">
                      <i :class="`fas ${contact.showDetails ? 'fa-chevron-up' : 'fa-chevron-down'}`"></i>
                    </button>
                  </div>
                  <div v-if="contact.showDetails" class="mt-2">
                    <p><strong>Mensaje:</strong> {{ contact.message }}</p>
                    <p><strong>Teléfono:</strong> {{ contact.phoneNum }}</p>
                    <p><strong>Enviado:</strong> {{ new Date(contact.dateSend).toLocaleDateString() }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <BackendPagination :current-page="currentPage" :total-pages="totalPages" @change-page="changePage" />
  </div>
</template>

<style scoped>
.card {
  border-radius: 10px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

button i {
  transition: transform 0.3s;
}

.contacts-scroll-container {
  max-height: 210px;
  overflow-y: auto;
  padding-right: 4px;
}

/* Optional: Nice scrollbar */
.contacts-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.contacts-scroll-container::-webkit-scrollbar-thumb {
  background-color: #bbb;
  border-radius: 3px;
}
</style>
